import { useState } from 'react';
import { errorMessage } from '../api/client';
import { usersApi, type UserForm, type UserPatch } from '../api/endpoints';
import type { User } from '../api/types';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';
import { Notice } from '../components/Notice';
import { ROLES, ROLE_HINTS, ROLE_LABELS, type Role } from '../domain/roles';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime } from '../lib/format';
import { useCurrentUser } from '../session/SessionContext';

function CreateUserModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<UserForm>({
    email: '',
    displayName: '',
    role: 'lecture',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    usersApi
      .create(form)
      .then(() => onSaved())
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Nouvel utilisateur"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-utilisateur"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Création…' : 'Créer le compte'}
          </button>
        </>
      }
    >
      <form id="formulaire-utilisateur" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}
        <div className="grille-champs">
          <Field label="Adresse électronique">
            <input
              type="email"
              value={form.email}
              required
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </Field>
          <Field label="Nom affiché">
            <input
              value={form.displayName}
              required
              maxLength={120}
              onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            />
          </Field>
          <Field label="Rôle" hint={ROLE_HINTS[form.role]}>
            <select
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Mot de passe" hint="12 caractères minimum.">
            <input
              type="password"
              value={form.password}
              required
              minLength={12}
              autoComplete="new-password"
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  isSelf,
  onClose,
  onSaved,
}: {
  user: User;
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [role, setRole] = useState<Role>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    const patch: UserPatch = {};
    if (displayName !== user.displayName) patch.displayName = displayName;
    if (role !== user.role) patch.role = role;
    if (isActive !== user.isActive) patch.isActive = isActive;
    if (password !== '') patch.password = password;

    if (Object.keys(patch).length === 0) {
      setError('Aucun champ modifié.');
      return;
    }

    setBusy(true);
    setError(null);
    usersApi
      .update(user.id, patch)
      .then(() => onSaved())
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={`Modifier ${user.displayName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-utilisateur-edition"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form
        id="formulaire-utilisateur-edition"
        className="pile pile--serree"
        onSubmit={onSubmit}
      >
        {error !== null && <Notice tone="erreur">{error}</Notice>}
        {isSelf && (
          <Notice tone="info">
            C'est votre propre compte : le rôle et l'état actif ne sont pas modifiables
            ici.
          </Notice>
        )}
        <div className="grille-champs">
          <Field label="Adresse électronique" hint="Non modifiable.">
            <input value={user.email} disabled />
          </Field>
          <Field label="Nom affiché">
            <input
              value={displayName}
              required
              maxLength={120}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </Field>
          <Field label="Rôle" hint={ROLE_HINTS[role]}>
            <select
              value={role}
              disabled={isSelf}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              {ROLES.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="État du compte">
            <select
              value={isActive ? 'actif' : 'desactive'}
              disabled={isSelf}
              onChange={(event) => setIsActive(event.target.value === 'actif')}
            >
              <option value="actif">Actif</option>
              <option value="desactive">Désactivé</option>
            </select>
          </Field>
          <Field
            label="Nouveau mot de passe"
            hint="Laisser vide pour ne pas le changer. Les sessions en cours seront closes."
            wide
          >
            <input
              type="password"
              value={password}
              minLength={12}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

export function AdminUsersPage() {
  const current = useCurrentUser();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const users = useAsync((signal) => usersApi.list(signal), []);

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Utilisateurs</h1>
          <p className="entete-page__sous-titre">
            Comptes d'accès à DiagFlow. Distincts des salariés de l'entreprise.
          </p>
        </div>
        <button
          type="button"
          className="bouton bouton--primaire"
          onClick={() => setCreating(true)}
        >
          Nouvel utilisateur
        </button>
      </div>

      {users.error !== null && (
        <Notice tone="erreur" title="Chargement impossible">
          {users.error}
        </Notice>
      )}

      <div className="panneau">
        <div className="tableau-cadre">
          <table className="tableau">
            <thead>
              <tr>
                <th>Nom affiché</th>
                <th>Adresse électronique</th>
                <th>Rôle</th>
                <th>État</th>
                <th>Créé le</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.loading && users.data === null && (
                <tr>
                  <td colSpan={6} className="tableau__vide">
                    Chargement…
                  </td>
                </tr>
              )}
              {users.data?.users.map((user) => (
                <tr key={user.id}>
                  <td>{user.displayName}</td>
                  <td className="mono">{user.email}</td>
                  <td>{ROLE_LABELS[user.role]}</td>
                  <td>
                    {user.isActive ? (
                      <Badge tone="ok">Actif</Badge>
                    ) : (
                      <Badge tone="neutre">Désactivé</Badge>
                    )}
                  </td>
                  <td className="tableau__nombre">{formatDateTime(user.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="bouton bouton--petit"
                      onClick={() => setEditing(user)}
                    >
                      Modifier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            users.reload();
          }}
        />
      )}

      {editing !== null && (
        <EditUserModal
          user={editing}
          isSelf={editing.id === current.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            users.reload();
          }}
        />
      )}
    </div>
  );
}
